import { useDocumentMeta } from '../document-meta.js';
import { catalogs, type TranslationCatalog } from '../i18n/index.js';
import { useOptionalLocale } from '../locale.js';

/**
 * The localised 500 page (issue #376).
 *
 * Rendered by both error boundaries, and also reachable as the `/500` route so
 * it can be reviewed and tested as a page rather than only as a fallback.
 *
 * IT DOES NOT KNOW WHAT WENT WRONG, on purpose. There is no prop for the thrown
 * value and no interpolation of one: the page says a render failed and offers
 * the two recoveries that actually work (retry the boundary, or reload the
 * document from the root). Printing the exception would be the disclosure the
 * boundary exists to prevent.
 *
 * TWO WAYS TO GET ITS COPY. Inside the app it reads the locale context. The
 * outermost boundary sits ABOVE `LocaleProvider` — it exists precisely for the
 * case where that provider threw — so it passes `catalog` explicitly, drawn
 * from the document's declared language instead. `useOptionalLocale` is what
 * lets the same component serve both without a second copy of this markup.
 *
 * The home control is a plain `<a href="/">`, not a router `<Link>`, because
 * the outermost boundary renders outside the router. On a healthy router the
 * difference is a full document load instead of a client transition — which is
 * the more reliable recovery from a render failure anyway, since it discards
 * whatever module or state got the app into that position.
 */
export function ServerError({
  onRetry,
  catalog,
}: {
  readonly onRetry?: () => void;
  readonly catalog?: TranslationCatalog;
}) {
  const context = useOptionalLocale();
  const t = catalog ?? context?.t ?? catalogs.en;
  useDocumentMeta(t.meta.serverError);
  return (
    <article className="error-page" role="alert">
      <h1 className="page-title">{t.errors.serverError}</h1>
      <p className="page-lead">{t.errors.serverErrorBody}</p>
      <div className="error-page__actions">
        {onRetry === undefined ? null : (
          <button type="button" className="rp-button rp-button--primary" onClick={onRetry}>
            {t.errors.retry}
          </button>
        )}
        <a className="rp-button rp-button--secondary" href="/">
          {t.brand.product}
        </a>
      </div>
    </article>
  );
}
